import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  ProgressBar,
  SectionHeader,
  StatCard,
} from "../components/design-system/index.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import {
  compileEmergencyDoc,
  createEstateItem,
  deleteEstateItem,
  getEstateUploadUrl,
  listBeneficiaryAudits,
  listEstateItems,
  updateEstateItem,
  type EstateDocumentItem,
  type EstateDocumentStatus,
} from "../lib/api-modules/estate.js";
import {
  ESTATE_DOCUMENT_TYPES,
  STATUS_LABEL,
  STATUS_NEXT,
  isStaleReview,
  latestDocumentsByType,
  summarizeReadiness,
} from "../lib/util/estate-readiness.js";

import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";
/**
 * /estate — Phase 9 readiness checklist + sub-domain hub.
 *
 * Renders the 7 canonical estate-document tiles with status badges +
 * upload + status-transition affordances, plus links to the four
 * sub-pages (letter of intent / digital assets / burial wishes) plus
 * the In-Case-of-Emergency PDF compile button + the latest beneficiary
 * audit summary tile.
 */

export function EstatePage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const itemsQ = useQuery({
    queryKey: ["estate-items"],
    queryFn: listEstateItems,
  });
  const auditsQ = useQuery({
    queryKey: ["estate-audits"],
    queryFn: listBeneficiaryAudits,
  });

  const items = itemsQ.data?.items ?? [];
  const summary = summarizeReadiness(items);
  const docByType = latestDocumentsByType(items);
  const latestAudit = auditsQ.data?.audits?.[0];

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-6">
      <SectionHeader
        title="Estate planning"
        sub="The documents your spouse + executor + guardians need if you can't walk them through it. Phase 9 — readiness checklist + document vault + quarterly beneficiary audit + emergency-doc compile."
      />

      <Toast message={toast.message} />

      {/* ── Top progress + tiles ────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Documents executed"
          value={`${summary.executed + summary.reviewed} / ${summary.total}`}
          sub={
            summary.staleReviewCount > 0
              ? `${summary.staleReviewCount} flagged for stale review`
              : "Tracking the 7 core documents"
          }
        />
        <StatCard
          label="In progress"
          value={`${summary.drafted}`}
          sub="Drafted, awaiting execution"
        />
        <StatCard
          label="Not yet drafted"
          value={`${summary.needed}`}
          sub="Document type has no record yet"
        />
      </div>
      <ProgressBar
        value={summary.executed + summary.reviewed}
        max={summary.total}
        ariaLabel="Documents executed"
      />

      {/* ── Stale review warning ────────────────────────────────────── */}
      {summary.staleReviewCount > 0 && (
        <Card className="border-[var(--color-warning)] bg-[var(--color-warning-soft)]/40">
          <p className="text-sm font-medium text-[var(--color-warning)]">
            {summary.staleReviewCount} document(s) reviewed over 2 years ago.
            Estate-planning consensus is to refresh every 1–2 years (or after a
            major life event).
          </p>
        </Card>
      )}

      {/* ── Per-document grid ───────────────────────────────────────── */}
      <section>
        <SectionHeader title="Documents" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {ESTATE_DOCUMENT_TYPES.map((t) => {
            const doc = docByType.get(t.type);
            return (
              <DocumentTile
                key={t.type}
                docType={t.type}
                label={t.label}
                description={t.description}
                doc={doc}
                onCreated={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ["estate-items"],
                  });
                  toast.show(`${t.label} created`);
                }}
                onUpdated={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ["estate-items"],
                  });
                  toast.show(`${t.label} updated`);
                }}
                onDeleted={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ["estate-items"],
                  });
                  toast.show("Document removed");
                }}
              />
            );
          })}
        </div>
      </section>

      {/* ── Sub-pages + emergency-doc ───────────────────────────────── */}
      <section>
        <SectionHeader title="Other estate records" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SubLinkCard
            to="/estate/letter-of-intent"
            label="Letter of intent"
            description="Owner-only. Encrypted with your tenant key. Free-form note for your daughter and the spouse who survives you."
          />
          <SubLinkCard
            to="/estate/digital-assets"
            label="Digital assets inventory"
            description="Per-account recovery instructions (vault location, recovery codes location). Never raw passwords."
          />
          <SubLinkCard
            to="/estate/burial-wishes"
            label="Burial wishes"
            description="Burial vs cremation, funeral home, plot location, religious considerations."
          />
          <EmergencyDocCard />
        </div>
      </section>

      {/* ── Beneficiary audit ───────────────────────────────────────── */}
      <section>
        <SectionHeader
          title="Beneficiary audit"
          sub="Hearth checks every beneficiary-bearing account quarterly (Jan / Apr / Jul / Oct 1, 8am ET). Flags missing designations, designations older than 2 years, or accounts naming 'Estate' (which forces probate)."
        />
        {latestAudit ? (
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-[var(--color-text)]">
                Latest audit — {latestAudit.audit_date}
              </p>
              <Pill
                tone={latestAudit.flagged_count === 0 ? "success" : "warning"}
              >
                {latestAudit.flagged_count === 0
                  ? "All clear"
                  : `${latestAudit.flagged_count} flagged`}
              </Pill>
            </div>
            <p className="mt-2 text-sm text-[var(--color-muted)]">
              {latestAudit.summary}
            </p>
            {latestAudit.flagged_accounts.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm">
                {latestAudit.flagged_accounts.map((a) => (
                  <li key={a.account_id} className="text-[var(--color-text)]">
                    <span className="font-medium">{a.account_name}</span>{" "}
                    <span className="text-[var(--color-muted)]">
                      ({a.account_type})
                    </span>{" "}
                    — {a.flags.join(" · ")}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ) : (
          <EmptyState
            title="No audit run yet"
            description="The first quarterly audit fires on the next Jan / Apr / Jul / Oct 1st at 8am ET. Audits will appear here as they run."
          />
        )}
      </section>
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.estate} />
      </div>
    </div>
  );
}

// === Document tile ========================================================

function DocumentTile(props: {
  docType: EstateDocumentItem["document_type"];
  label: string;
  description: string;
  doc: EstateDocumentItem | undefined;
  onCreated: () => void;
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const { docType, label, description, doc, onCreated, onUpdated, onDeleted } =
    props;
  const createMut = useMutation({
    mutationFn: () =>
      createEstateItem({
        item_type: "document",
        document_type: docType,
        status: "needed",
        display_name: label,
      }),
    onSuccess: () => onCreated(),
  });
  const statusMut = useMutation({
    mutationFn: (status: EstateDocumentStatus) =>
      updateEstateItem(doc!.item_id, { status }),
    onSuccess: () => onUpdated(),
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteEstateItem(doc!.item_id),
    onSuccess: () => onDeleted(),
  });
  const stale = doc && doc.status === "reviewed" && isStaleReview(doc.last_reviewed_at);
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-[var(--color-text)]">{label}</p>
          <p className="mt-1 text-xs text-[var(--color-muted)]">{description}</p>
        </div>
        {doc ? (
          <Pill
            tone={
              stale
                ? "warning"
                : doc.status === "executed" || doc.status === "reviewed"
                  ? "success"
                  : doc.status === "drafted"
                    ? "accent"
                    : "neutral"
            }
          >
            {stale ? "Stale review" : STATUS_LABEL[doc.status]}
          </Pill>
        ) : (
          <Pill tone="neutral">Not started</Pill>
        )}
      </div>
      {doc?.last_reviewed_at && (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Last reviewed {doc.last_reviewed_at.slice(0, 10)}
        </p>
      )}
      {doc?.s3_object_key && (
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Uploaded:{" "}
          <span className="font-mono">
            {doc.original_filename ?? doc.s3_object_key.split("/").pop()}
          </span>
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!doc && (
          <Button
            size="sm"
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending}
          >
            Start tracking
          </Button>
        )}
        {doc && (
          <>
            <UploadButton item={doc} onUploaded={onUpdated} />
            {STATUS_NEXT[doc.status].map((next) => (
              <Button
                key={next}
                size="sm"
                variant="secondary"
                onClick={() => statusMut.mutate(next)}
                disabled={statusMut.isPending}
              >
                Move to {STATUS_LABEL[next]}
              </Button>
            ))}
            <ConfirmButton
              label="Remove"
              confirmLabel="Yes, remove"
              onConfirm={() => deleteMut.mutate()}
              isPending={deleteMut.isPending}
            />
          </>
        )}
      </div>
    </Card>
  );
}

// === Upload button ========================================================

function UploadButton({
  item,
  onUploaded,
}: {
  item: EstateDocumentItem;
  onUploaded: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-uploaded
    if (!file) return;
    setBusy(true);
    try {
      const presigned = await getEstateUploadUrl(item.item_id, {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        byte_size: file.size,
      });
      const putRes = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: presigned.required_headers,
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed: HTTP ${putRes.status}`);
      }
      // Patch the row with the upload metadata so the row reflects what's
      // in S3. The handler accepts these on PATCH (see api/estate/update.ts).
      await updateEstateItem(item.item_id, {
        s3_object_key: presigned.s3_object_key,
        s3_uploaded_at: new Date().toISOString(),
        original_filename: file.name,
        content_type: file.type || "application/octet-stream",
        byte_size: file.size,
      });
      toast.show(`Uploaded ${file.name}`);
      onUploaded();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className="inline-flex">
      <input
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/heic,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={(e) => void onChange(e)}
        disabled={busy}
        className="hidden"
      />
      <span className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-2.5 py-1.5 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]">
        {busy ? "Uploading…" : item.s3_object_key ? "Replace file" : "Upload file"}
      </span>
    </label>
  );
}

// === Sub-page link card ===================================================

function SubLinkCard({
  to,
  label,
  description,
}: {
  to: string;
  label: string;
  description: string;
}) {
  return (
    <Link to={to} className="block">
      <Card className="h-full transition-colors hover:bg-[var(--color-elevated)]">
        <p className="font-medium text-[var(--color-accent)]">{label} →</p>
        <p className="mt-1 text-xs text-[var(--color-muted)]">{description}</p>
      </Card>
    </Link>
  );
}

// === Emergency-doc compile card ===========================================

function EmergencyDocCard() {
  const toast = useToast();
  const compileMut = useMutation({
    mutationFn: () => compileEmergencyDoc(),
    onSuccess: (resp) => toast.show(resp.message || "Compilation queued"),
    onError: (err) =>
      toast.show(err instanceof Error ? err.message : "Compile failed"),
  });
  return (
    <Card>
      <p className="font-medium text-[var(--color-accent)]">
        In-Case-of-Emergency PDF
      </p>
      <p className="mt-1 text-xs text-[var(--color-muted)]">
        Compiles family + accounts + key contacts + digital-assets recovery +
        burial wishes + your letter of intent into a single PDF. Emails you a
        60-minute presigned download link. Save locally + share location with
        whoever needs it.
      </p>
      <div className="mt-3">
        <Button
          size="sm"
          onClick={() => compileMut.mutate()}
          disabled={compileMut.isPending}
        >
          {compileMut.isPending ? "Queuing…" : "Compile + email"}
        </Button>
      </div>
    </Card>
  );
}
